<template>
  <div v-if="!loading">
    <v-container fluid>
      <v-card elevation="3" class="mb-6">
        <v-card-title class="primary white--text py-4">
          <v-icon left dark large>
            mdi-certificate-outline
          </v-icon>
          <div>
            <div class="text-h6">Základný resocializačný a výchovno vzdelávací program</div>
            <div class="text-caption font-weight-light">Evidencia a hodnotenie absolvovania základného programu</div>
          </div>
        </v-card-title>

        <!-- Info Alert -->
        <v-alert
          border="left"
          colored-border
          color="info"
          elevation="1"
          class="ma-4"
        >
          <div class="text-body-2">
            <div class="mb-2">
              <v-icon small color="info" class="mr-2">mdi-calendar-start</v-icon>
              <strong>Dátum zaradenia:</strong> označí sa dátum realizácie prvej hodiny v module
            </div>
            <div class="mb-2">
              <v-icon small color="info" class="mr-2">mdi-calendar-end</v-icon>
              <strong>Dátum vyradenia:</strong> označí sa dátum realizácie poslednej hodiny v module
            </div>
            <div class="mb-2">
              <v-icon small color="info" class="mr-2">mdi-clock-check-outline</v-icon>
              <strong>Počet absolvovaných hodín:</strong> uvádza sa počet hodín ktoré odsúdený absolvoval v danom module
            </div>
            <div>
              <v-icon small color="info" class="mr-2">mdi-text-box-outline</v-icon>
              <strong>Dôvod neabsolvovania:</strong> uvádza sa písomne odôvodnenie (napr. PN, eskorty, odpor ods. voči téme a pod.)
            </div>
          </div>
        </v-alert>

        <v-card-text class="px-6 pb-6">
          <!-- Date and Summary Row -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <date-picker-programy
                v-model="form.datum_zaradenia"
                label="Dátum začiatku ZRaVVP"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success">mdi-calendar-start</v-icon>
                </template>
              </date-picker-programy>
            </v-col>
            <v-col cols="12" md="4">
              <date-picker-programy
                v-model="form.datum_vyradenia"
                label="Dátum ukončenia ZRaVVP"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="error">mdi-calendar-end</v-icon>
                </template>
              </date-picker-programy>
            </v-col>
            <v-col cols="12" md="4">
              <v-card color="blue-grey lighten-5" flat>
                <v-card-text class="text-center py-3">
                  <div class="text-h4 primary--text font-weight-bold">
                    {{ form.celkovy_pocet_absolvovanych_hodin || '0' }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">
                    Celkový počet absolvovaných hodín
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Programs Table -->
          <v-divider class="mb-4" />
          <div class="text-subtitle-1 font-weight-medium mb-3">
            <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
            Moduly programu
          </div>
          
          <v-card
            v-for="(item, index) in form.zakladne_programy"
            :key="item.name"
            outlined
            class="mb-3"
            :class="{ 'elevation-2': hoveredIndex === index }"
            @mouseenter="hoveredIndex = index"
            @mouseleave="hoveredIndex = null"
          >
            <v-card-text class="pa-4">
              <v-row align="center">
                <v-col cols="12" md="4">
                  <div class="d-flex align-center">
                    <v-avatar size="40" color="primary" class="mr-3">
                      <span class="white--text font-weight-bold">{{ item.pocet_hodin || '0' }}</span>
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-1 font-weight-medium">{{ item.nazov }}</div>
                      <div class="text-caption grey--text">Modul {{ index + 1 }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="item.pocet_hodin"
                    label="Počet absolvovaných hodín"
                    type="number"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="mdi-clock-outline"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-textarea
                    v-model="item.dovod_neabsolvovania_stanoveho"
                    label="Dôvod neabsolvovania stanoveného počtu hodín"
                    outlined
                    dense
                    auto-grow
                    rows="1"
                    hide-details
                    prepend-inner-icon="mdi-comment-text-outline"
                    placeholder="Zadajte dôvod, ak neboli absolvované všetky hodiny..."
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>

      <v-card elevation="3" class="my-6">
        <v-card-title class="indigo darken-1 white--text py-4">
          <v-icon left dark large>
            mdi-school-outline
          </v-icon>
          <div>
            <div class="text-h6">Špecializované resocializačné a výchovno vzdelávacie programy</div>
            <div class="text-caption font-weight-light">Evidencia a hodnotenie špecializovaných programov</div>
          </div>
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                dark
                v-bind="attrs"
                v-on="on"
                download
                href="/charakteristika_SRaVVP_final.pdf"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Stiahnuť charakteristiku programov</span>
          </v-tooltip>
        </v-card-title>

        <v-card-text class="px-6 pb-6">
          <!-- Statistics Cards -->
          <v-row class="mb-6">
            <v-col cols="12" sm="6" md="3">
              <v-card color="blue-grey lighten-5" flat>
                <v-card-text class="text-center py-4">
                  <v-icon size="40" color="blue-grey darken-2">mdi-apps</v-icon>
                  <div class="text-h4 font-weight-bold mt-2 blue-grey--text text--darken-2">
                    {{ form.specializovane_programy.length }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">Celkový počet programov</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="blue lighten-5" flat>
                <v-card-text class="text-center py-4">
                  <v-icon size="40" color="primary">mdi-calendar-check</v-icon>
                  <div class="text-h4 font-weight-bold mt-2 primary--text">
                    {{ enrolledCount }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">Zaradených programov</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="green lighten-5" flat>
                <v-card-text class="text-center py-4">
                  <v-icon size="40" color="success">mdi-check-circle</v-icon>
                  <div class="text-h4 font-weight-bold mt-2 success--text">
                    {{ completedCount }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">Absolvovaných programov</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-card color="orange lighten-5" flat>
                <v-card-text class="text-center py-4">
                  <v-icon size="40" color="warning">mdi-star</v-icon>
                  <div class="text-h4 font-weight-bold mt-2 warning--text">
                    {{ recommendedCount }}
                  </div>
                  <div class="text-caption grey--text text--darken-1">Odporúčaných programov</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Empty State -->
          <v-alert
            v-if="form.specializovane_programy.length === 0"
            type="info"
            text
            prominent
            border="left"
            icon="mdi-information"
          >
            <div class="text-h6">Žiadne programy</div>
            <div>Momentálne nie sú k dispozícii žiadne špecializované programy.</div>
          </v-alert>

          <!-- Programs List -->
          <v-expansion-panels
            v-if="form.specializovane_programy.length > 0"
            v-model="expandedPanel"
            accordion
            multiple
            flat
          >
            <v-expansion-panel
              v-for="(item, index) in form.specializovane_programy"
              :key="index"
              class="mb-3 elevation-2"
            >
              <v-expansion-panel-header class="pa-4">
                <div class="d-flex align-center" style="width: 100%">
                  <div class="flex-grow-1">
                    <div class="d-flex align-center mb-1">
                      <v-chip
                        small
                        :color="getProgramStatusColor(item)"
                        text-color="white"
                        class="mr-3"
                      >
                        <v-icon left x-small>
                          {{ getProgramStatusIcon(item) }}
                        </v-icon>
                        {{ getProgramStatus(item) }}
                      </v-chip>
                      <span class="text-subtitle-1 font-weight-medium">{{ item.nazov }}</span>
                    </div>
                    <div v-if="item.zaradeny_do_sravvp" class="text-caption grey--text ml-1">
                      <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
                      <span v-if="item.datum_zaradenia">{{ item.datum_zaradenia }}</span>
                      <span v-if="item.datum_zaradenia && item.datum_vyradenia"> - </span>
                      <span v-if="item.datum_vyradenia">{{ item.datum_vyradenia }}</span>
                      <span v-if="item.pocet_hodin" class="ml-3">
                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                        {{ item.pocet_hodin }} hodín
                      </span>
                    </div>
                  </div>
                  <div class="text-right ml-4" style="min-width: 100px">
                    <v-progress-circular
                      :value="item.expertne_posudenie"
                      :color="getScoreColor(item.expertne_posudenie)"
                      :size="50"
                      :width="5"
                    >
                      <span class="text-caption font-weight-bold">{{ item.expertne_posudenie }}%</span>
                    </v-progress-circular>
                    <div class="text-caption mt-1" :class="getScoreColor(item.expertne_posudenie) + '--text'">
                      {{ getScoreLabel(item.expertne_posudenie) }}
                    </div>
                  </div>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content>
                <v-card flat>
                  <v-card-text>
                    <!-- Warning for recommended but not enrolled -->
                    <v-alert
                      v-if="item.expertne_posudenie >= 70 && !item.zaradeny_do_sravvp"
                      type="warning"
                      text
                      border="left"
                      class="mb-4"
                    >
                      <div class="d-flex align-center">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        <div>
                          <div class="font-weight-medium">Program je vysoko odporúčaný</div>
                          <div class="text-caption">Zvážte zaradenie klienta do tohto programu na základe expertného posúdenia.</div>
                        </div>
                      </div>
                    </v-alert>

                    <!-- Info for enrolled programs -->
                    <v-alert
                      v-if="item.zaradeny_do_sravvp && !item.absolvoval"
                      type="info"
                      text
                      border="left"
                      class="mb-4"
                    >
                      <div class="d-flex align-center">
                        <v-icon color="info" class="mr-2">mdi-information</v-icon>
                        <div>
                          <div class="font-weight-medium">Program prebieha</div>
                          <div class="text-caption">Klient je aktuálne zaradený v tomto programe.</div>
                        </div>
                      </div>
                    </v-alert>

                    <!-- Success for completed programs -->
                    <v-alert
                      v-if="item.absolvoval"
                      type="success"
                      text
                      border="left"
                      class="mb-4"
                    >
                      <div class="d-flex align-center">
                        <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                        <div>
                          <div class="font-weight-medium">Program úspešne absolvovaný</div>
                          <div class="text-caption">Klient úspešne dokončil tento program.</div>
                        </div>
                      </div>
                    </v-alert>

                    <!-- Expert Assessment Slider -->
                    <v-row>
                      <v-col cols="12">
                        <div class="text-subtitle-2 mb-2">
                          Expertné posúdenie potreby programu
                        </div>
                        <v-slider
                          v-model="item.expertne_posudenie"
                          :color="getScoreColor(item.expertne_posudenie)"
                          min="0"
                          max="100"
                          thumb-label="always"
                          :readonly="form.closed"
                        >
                          <template v-slot:prepend>
                            <v-icon :color="getScoreColor(item.expertne_posudenie)">
                              {{ getScoreIcon(item.expertne_posudenie) }}
                            </v-icon>
                          </template>
                          <template v-slot:append>
                            <v-chip
                              small
                              :color="getScoreColor(item.expertne_posudenie)"
                              text-color="white"
                            >
                              {{ getScoreLabel(item.expertne_posudenie) }}
                            </v-chip>
                          </template>
                        </v-slider>
                      </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <!-- Program Status -->
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-switch
                          v-model="item.zaradeny_do_sravvp"
                          :disabled="form.closed"
                          label="Zaradený do ŠRaVVP"
                          color="primary"
                          :hint="item.zaradeny_do_sravvp ? 'Program je aktívny' : 'Program nie je zaradený'"
                          persistent-hint
                        >
                          <template v-slot:prepend>
                            <v-icon :color="item.zaradeny_do_sravvp ? 'primary' : 'grey'">
                              mdi-calendar-plus
                            </v-icon>
                          </template>
                        </v-switch>
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-switch
                          v-model="item.absolvoval"
                          :disabled="form.closed || !item.zaradeny_do_sravvp"
                          label="Absolvoval ŠRaVVP"
                          color="success"
                          :hint="item.absolvoval ? 'Program ukončený' : 'Program prebieha alebo nebol dokončený'"
                          persistent-hint
                        >
                          <template v-slot:prepend>
                            <v-icon :color="item.absolvoval ? 'success' : 'grey'">
                              mdi-check-circle
                            </v-icon>
                          </template>
                        </v-switch>
                      </v-col>
                    </v-row>

                    <!-- Program Details (shown when enrolled) -->
                    <v-expand-transition>
                      <div v-if="item.zaradeny_do_sravvp">
                        <v-divider class="my-4" />
                        <v-row dense>
                          <v-col
                            cols="12"
                            md="4"
                          >
                            <date-picker-programy
                              v-model="item.datum_zaradenia"
                              label="Dátum začiatku"
                              :disabled="form.closed"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            md="4"
                          >
                            <date-picker-programy
                              v-model="item.datum_vyradenia"
                              label="Dátum ukončenia"
                              :disabled="form.closed"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            md="4"
                          >
                            <v-text-field
                              v-model="item.pocet_hodin"
                              label="Počet absolvovaných hodín"
                              type="number"
                              outlined
                              dense
                              :disabled="form.closed"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon small>
                                  mdi-clock-outline
                                </v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </div>
                    </v-expand-transition>

                    <!-- Notes/Reasons -->
                    <v-expand-transition>
                      <div v-if="item.zaradeny_do_sravvp && !item.absolvoval">
                        <v-divider class="my-4" />
                        <v-textarea
                          v-model="item.dovod_neabsolvovania"
                          label="Dôvod neabsolvovania"
                          outlined
                          auto-grow
                          rows="2"
                          :disabled="form.closed"
                          hint="Uveďte dôvod, prečo program nebol dokončený"
                          persistent-hint
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="warning">
                              mdi-alert-circle-outline
                            </v-icon>
                          </template>
                        </v-textarea>
                      </div>
                    </v-expand-transition>

                    <v-expand-transition>
                      <div v-if="(!item.zaradeny_do_sravvp && isHighest(item)) || displayWarning(item)">
                        <v-divider class="my-4" />
                        <v-textarea
                          v-model="item.dovod_nezaradenia"
                          label="Dôvod nezaradenia do programu"
                          outlined
                          auto-grow
                          rows="2"
                          :disabled="form.closed"
                          hint="Program je odporúčaný (vysoké skóre), uveďte dôvod nezaradenia"
                          persistent-hint
                        >
                          <template v-slot:prepend-inner>
                            <v-icon color="error">
                              mdi-information-outline
                            </v-icon>
                          </template>
                        </v-textarea>
                      </div>
                    </v-expand-transition>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <v-card v-if="form.indication_scores" elevation="3" class="my-6">
        <v-card-title class="teal darken-1 white--text py-4">
          <v-icon left dark large>
            mdi-clipboard-check-outline
          </v-icon>
          <div>
            <div class="text-h6">Indikačný nástroj</div>
            <div class="text-caption font-weight-light">Hodnotenie potrieb klienta</div>
          </div>
        </v-card-title>
        <v-card-text class="px-6 pb-6">
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left text-subtitle-2">
                    <v-icon small class="mr-1">mdi-tag</v-icon>
                    Názov oblasti
                  </th>
                  <th class="text-left text-subtitle-2">
                    <v-icon small class="mr-1">mdi-chart-bar</v-icon>
                    Skóre
                  </th>
                  <th class="text-left text-subtitle-2">
                    <v-icon small class="mr-1">mdi-checkbox-marked-circle</v-icon>
                    Stav
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, key) in form.indication_scores"
                  :key="key"
                >
                  <td>{{ key }}</td>

                  <td>
                    <v-slider
                      v-model="item.score"
                      class="pt-3"
                      :color="color(key, 2)"
                      min="0"
                      max="100"
                      thumb-label="always"
                      readonly
                    />
                  </td>
                  <td>
                    <v-checkbox
                      v-model="item.complete"
                      readonly
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Form Actions -->
      <v-divider class="my-6" />
      <FormActions
        v-if="!form.closed"
        ref="formActions"
        :fixed="false"
        :is-dirty="isDirty"
        :loading="loading"
        save-text="Uložiť"
        @save="updateItem"
      />
    </v-container>

    <!-- Info Dialog -->
    </div>

  <div v-else>
    <form-skeleton-component skeleton_type="SocialneProgramy" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from '@vue/composition-api';
import { useRoute } from '@/composables/useRouter.js';
import { APIService } from "@/http/APIService.js";
import FormSkeleton from "../components/FormSkeleton.vue";
import DatePickerProgramy from "@/components/DatePickerProgramy.vue";
import FormActions from "@/components/FormActions.vue";
import { useIapszForm } from "@/composables/useIapszForm.js";
import { useFormDirty } from "@/composables/useFormDirty.js";

const apiService = new APIService();

export default {
  components: {
    "form-skeleton-component": FormSkeleton,
    DatePickerProgramy,
    FormActions,
  },

  setup() {
    const route = useRoute();
    
    const { loading, setLoading, handleSaveSuccess, handleSaveError } = useIapszForm();
    const formActions = ref(null);
    
    const form = ref({
      id: null,
      closed: false,
      specializovane_programy: [],
      indication_scores: {},
    });
    
    const initialForm = ref({});
    const { isDirty, markAsPristine, enableBrowserWarning } = useFormDirty(initialForm, form, { deep: true });
    enableBrowserWarning();


    const selects = reactive({
      nepravdive_zavadzajuce: [
        "podľa výsledkov náhodného overenia vybraných informácii poskytovaných odsúdeným odsúdený UVIEDOL nepravdivé/zavádzajúce informácie pri hodnotení, vo vstupnom dotazníku a pri pohovoroch",
        "podľa výsledkov náhodného overenia vybraných informácii poskytovaných odsúdeným odsúdený NEUVIEDOL nepravdivé/zavádzajúce informácie pri hodnotení, vo vstupnom dotazníku a pri pohovoroch",
      ],
    });

    const manual = reactive({
      zakladny_program: {
        nazov: "Základný resocializačný a výchovno vzdelávací program",
        charakteristika: [
          "Dátum zaradenia – označí sa dátum realizácie prvej hodiny v module základného resocializačného a výchovno vzdelávacieho programu",
          "Dátum vyradenia – označí sa dátum realizácie poslednej hodiny v module základného resocializačného a výchovno vzdelávacieho programu",
          "Absolvoval – označí sa v prípade absolvovania modulu v stanovenom počte hodín",
          "Počet absolvovaných hodín – uvádza sa počet hodín ktoré odsúdený absolvoval v danom module",
          "Dôvod neabsolvovania stanoveného počtu hodín",
          "Uvádza sa písomne odôvodnenie neabsolvovania stanoveného počtu hodín (napr. PN, eskorty, odpor ods. voči téme a pod.)"
        ],
      },
      specializovane_programy: {
        nazov: "Špecializované resocializačné a výchovno vzdelávacie programy",
        charakteristika: [
          "Expertné posúdenie potreby programu – po kolektívnej dohode je určená potreba absolvovania daného programu",
          "Zaradený do ŠRaVVP - označí sa v prípade zaradenia do ŠRaVVP",
          "Absolvoval program - označí sa v prípade absolvovania programu stanovenom počte hodín",
          "Poznámky – priestor pre informácie a zdôvodnenia pri nezaradení a neabsolvovaní programu v prípade indikácie (určenia potreby realizovať program)"
        ],
      },
    });
    
    // Initialize info dialog with manual data
    // State for expansion panels
    const expandedPanel = ref([]);
    const hoveredIndex = ref(null);

    // Computed properties for summary stats
    const enrolledCount = computed(() => {
      return form.value.specializovane_programy?.filter(p => p.zaradeny_do_sravvp).length || 0;
    });

    const completedCount = computed(() => {
      return form.value.specializovane_programy?.filter(p => p.absolvoval).length || 0;
    });

    const recommendedCount = computed(() => {
      return form.value.specializovane_programy?.filter(p => p.expertne_posudenie >= 70).length || 0;
    });

    // Helper methods for program status
    const getProgramStatus = (item) => {
      if (item.absolvoval) return 'Absolvovaný';
      if (item.zaradeny_do_sravvp) return 'Zaradený';
      if (item.expertne_posudenie >= 70) return 'Odporúčaný';
      return 'Nezaradený';
    };

    const getProgramStatusColor = (item) => {
      if (item.absolvoval) return 'success';
      if (item.zaradeny_do_sravvp) return 'primary';
      if (item.expertne_posudenie >= 70) return 'warning';
      return 'grey';
    };

    const getProgramStatusIcon = (item) => {
      if (item.absolvoval) return 'mdi-check-circle';
      if (item.zaradeny_do_sravvp) return 'mdi-school';
      if (item.expertne_posudenie >= 70) return 'mdi-star';
      return 'mdi-circle-outline';
    };

    const getScoreColor = (score) => {
      if (score >= 80) return 'green';
      if (score >= 50) return 'blue';
      if (score >= 20) return 'orange';
      return 'red';
    };

    const getScoreIcon = (score) => {
      if (score >= 80) return 'mdi-emoticon-happy';
      if (score >= 50) return 'mdi-emoticon-neutral';
      if (score >= 20) return 'mdi-emoticon-sad';
      return 'mdi-alert-circle';
    };

    const getScoreLabel = (score) => {
      if (score >= 80) return 'Vysoká potreba';
      if (score >= 50) return 'Stredná potreba';
      if (score >= 20) return 'Nízka potreba';
      return 'Minimálna potreba';
    };

    const displayWarning = (item) => {
      if (item.zaradeny_do_sravvp) return false;

      const sorted = [...form.value.specializovane_programy].sort(
        (a, b) => b.expertne_posudenie - a.expertne_posudenie
      );

      const itemIndex = sorted.findIndex((x) => x === item);

      if (itemIndex < 2 && sorted[2].zaradeny_do_sravvp) return true;

      return false;
    };

    const isHighest = (item) => {
      const highest = Math.max.apply(
        Math,
        form.value.specializovane_programy.map(function (o) {
          return o.expertne_posudenie;
        })
      );
      return highest == item.expertne_posudenie;
    };
    
    const color = (index, type) => {
      let value = 0;
      if (type == 1)
        value = form.value.specializovane_programy[index].expertne_posudenie;
      else
        value = form.value.indication_scores[index].score;

      if (value <= 20) return "red";
      else if (value <= 50) return "orange";
      else if (value <= 80) return "blue";
      else return "green";
    };

    const getItem = () => {
      setLoading(true);
      apiService.getSocialneProgramy(route.params.id).then((response) => {
        form.value = response;
        initialForm.value = JSON.parse(JSON.stringify(response));
        markAsPristine();
        setLoading(false);
      });
    };

    const updateItem = () => {
      apiService.updateSocialneProgramy(form.value, route.params.id).then(
        (response) => {
          handleSaveSuccess();
          if (formActions.value) {
            formActions.value.showSuccess();
          }
          form.value = response;
          markAsPristine();
        },
        () => {
          handleSaveError();
          if (formActions.value) {
            formActions.value.showError();
          }
        }
      );
    };

    onMounted(() => {
      getItem();
    });

    return {
      loading,
      form,
      selects,
      isDirty,
      formActions,
      expandedPanel,
      hoveredIndex,
      enrolledCount,
      completedCount,
      recommendedCount,
      displayWarning,
      isHighest,
      color,
      updateItem,
      getProgramStatus,
      getProgramStatusColor,
      getProgramStatusIcon,
      getScoreColor,
      getScoreIcon,
      getScoreLabel,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      if (window.confirm('Máte neuložené zmeny. Naozaj chcete odísť?')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>